import { useCallback, useState } from "react";
import { useLatestRef } from "@design-system/hooks/useLatestRef";
import type { BoardLayout, BoardLayoutCacheOf } from "./boardLayoutCache";

interface UseBoardLayoutCacheStateArgs<TCollapsed> {
    /** What storage holds for the current scope; undefined when unset or unparsable. */
    storedCache: BoardLayoutCacheOf<TCollapsed> | undefined;
    setStoredCache: (cache: BoardLayoutCacheOf<TCollapsed>) => void;
    /** The workspace or organization the cache belongs to. */
    scopeKey: string;
    defaultCache: BoardLayoutCacheOf<TCollapsed>;
}

/**
 * The layout and the collapse state a user keeps on a page, read from and
 * written to its storage. Kept in state too, since the storage hooks only
 * read on mount; a scope switch reads the next scope's. Each write starts
 * from the last one, rendered or not, so writes in the same tick compose.
 */
export const useBoardLayoutCacheState = <TCollapsed>({
    storedCache,
    setStoredCache,
    scopeKey,
    defaultCache,
}: UseBoardLayoutCacheStateArgs<TCollapsed>) => {
    const [updatedCache, setUpdatedCache] = useState<{
        scopeKey: string;
        cache: BoardLayoutCacheOf<TCollapsed>;
    } | null>(null);

    const cache =
        (updatedCache?.scopeKey === scopeKey ? updatedCache.cache : null) ??
        storedCache ??
        defaultCache;
    const latestCacheRef = useLatestRef(cache);

    const writeCache = useCallback(
        (
            update: (
                latestCache: BoardLayoutCacheOf<TCollapsed>,
            ) => BoardLayoutCacheOf<TCollapsed>,
        ) => {
            const latestCache = latestCacheRef.current;
            const nextCache = update(latestCache);
            if (nextCache === latestCache) {
                return;
            }
            latestCacheRef.current = nextCache;
            setUpdatedCache({ scopeKey, cache: nextCache });
            setStoredCache(nextCache);
        },
        [latestCacheRef, scopeKey, setStoredCache],
    );

    const setLayout = useCallback(
        (layout: BoardLayout) =>
            writeCache((latestCache) => ({ ...latestCache, layout })),
        [writeCache],
    );

    /** Writes the collapse state `update` makes from the latest one; nothing when it returns that same state. */
    const updateCollapsed = useCallback(
        (update: (collapsed: TCollapsed) => TCollapsed) =>
            writeCache((latestCache) => {
                const board = update(latestCache.board);
                return board === latestCache.board
                    ? latestCache
                    : { ...latestCache, board };
            }),
        [writeCache],
    );

    return {
        layout: cache.layout,
        collapsed: cache.board,
        setLayout,
        updateCollapsed,
    };
};
